/**
 * Load from the API, optionally polling. Polling pauses while the tab is
 * hidden: a forgotten real-time tab should not keep the database busy.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type ApiError } from './api';

export interface ApiState<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  reload: () => void;
}

export function useApi<T>(path: string | null, opts: { pollMs?: number } = {}): ApiState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState(path !== null);
  const [tick, setTick] = useState(0);
  const latest = useRef(path);
  latest.current = path;

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    setLoading(true);
    api<T>(path)
      .then((d) => {
        if (cancelled || latest.current !== path) return;
        setData(d);
        setError(undefined);
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, tick]);

  useEffect(() => {
    if (!opts.pollMs || path === null) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') reload();
    }, opts.pollMs);
    const onVisible = () => document.visibilityState === 'visible' && reload();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [opts.pollMs, path, reload]);

  return { data, error, loading, reload };
}
